import type { RoleClaimResponse, PermissionRegistry } from '../../types/role';

export const OTHER_MODULE = 'Other';

export interface PermissionItem {
  label: string;
  claim: RoleClaimResponse;
}
export interface PermissionSection {
  key: string;
  title?: string;
  description?: string;
  items: PermissionItem[];
}
export interface PermissionGroup {
  name: string;
  sections: PermissionSection[];
}

export const claimAction = (c: RoleClaimResponse) => c.value?.split('.')[2] ?? c.value;

// Simple fallback grouping by the second segment of the claim value
// (e.g. "Permissions.Attendance.Read" -> "Attendance"), used until the registry loads.
export function buildFallbackGroups(claims: RoleClaimResponse[]): PermissionGroup[] {
  const map = new Map<string, PermissionItem[]>();
  for (const claim of claims) {
    const module = claim.value?.split('.')[1] ?? claim.group ?? 'General';
    if (!map.has(module)) map.set(module, []);
    map.get(module)!.push({ label: claimAction(claim), claim });
  }
  return Array.from(map.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([name, items]) => ({ name, sections: [{ key: name, items }] }));
}

// Registry grouping: module -> feature -> allowed actions. Claims the registry
// does not know about go under "Other" so they are never hidden.
export function buildRegistryGroups(claims: RoleClaimResponse[], registry: PermissionRegistry): PermissionGroup[] {
  const byValue = new Map(claims.map((c) => [c.value, c]));
  const used = new Set<string>();
  const groups: PermissionGroup[] = [];
  for (const mod of registry.modules) {
    const sections: PermissionSection[] = [];
    for (const feature of mod.features) {
      const items: PermissionItem[] = [];
      for (const a of feature.actions) {
        const claim = byValue.get(a.permission);
        if (claim) {
          items.push({ label: a.action, claim });
          used.add(a.permission);
        }
      }
      if (items.length) {
        sections.push({ key: feature.key, title: feature.displayName, description: feature.description, items });
      }
    }
    if (sections.length) groups.push({ name: mod.name, sections });
  }
  const other = claims.filter((c) => !used.has(c.value));
  if (other.length) {
    groups.push({
      name: OTHER_MODULE,
      sections: [{ key: OTHER_MODULE, items: other.map((claim) => ({ label: claimAction(claim), claim })) }],
    });
  }
  return groups;
}

/**
 * Narrows groups to what the user is looking for. A query that matches a module or
 * feature name keeps all of its actions; otherwise only matching actions stay.
 * `onlySelected` hides everything that is not ticked. Empty sections/modules are dropped.
 */
export function filterGroups(groups: PermissionGroup[], query: string, onlySelected: boolean): PermissionGroup[] {
  const q = query.trim().toLowerCase();
  if (!q && !onlySelected) return groups;

  const result: PermissionGroup[] = [];
  for (const group of groups) {
    const moduleMatch = !!q && group.name.toLowerCase().includes(q);
    const sections: PermissionSection[] = [];
    for (const section of group.sections) {
      const sectionMatch =
        !!q &&
        (section.title?.toLowerCase().includes(q) || section.description?.toLowerCase().includes(q) || false);
      const items = section.items.filter(({ label, claim }) => {
        if (onlySelected && !claim.selected) return false;
        if (!q || moduleMatch || sectionMatch) return true;
        return (
          label.toLowerCase().includes(q) ||
          claim.value?.toLowerCase().includes(q) ||
          claim.description?.toLowerCase().includes(q) ||
          false
        );
      });
      if (items.length) sections.push({ ...section, items });
    }
    if (sections.length) result.push({ ...group, sections });
  }
  return result;
}

export const groupClaims = (group: PermissionGroup) =>
  group.sections.flatMap((section) => section.items.map((item) => item.claim));
